import React, { useState } from 'react';
import { View, TouchableWithoutFeedback } from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { slice as _slice, isEmpty as _isEmpty, get as _get, isNil as _isNil } from 'lodash';
import PropTypes from 'prop-types';

import { VFText, Icon, VFButton } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import ConfirmationQuickAction from '../../../../Common/UI/ConfirmationQuickAction/ConfirmationQuickAction';
import { PRODUCT_STATUS } from '../../../../Services/Enums';
import { getThemeImages } from '../../../../Themes';
import SohoFrameworkTestKeys from '../../../../Utils/SohoFrameworkTestKeys';
import PlanContractInfo from '../PlanContractInfo';
import PlanInfo from '../PlanInfo';
import PlanInfoQuickView from '../PlanInfoQuickView';
import UsagePercentageView from '../UsagePercentageView';
import UsageView from '../UsageView';

import styles from './PlanCard.Styles';

function PlanCard({
  id,
  type,
  name,
  status,
  renewalDate,
  usersIcon,
  withPercentageView,
  hideProgressBars,
  usageData,
  price,
  currency,
  isCurrencyFirst,
  withPrice,
  withPriceVat,
  contractData,
  users,
  unassignedUsers,
  withViewPlanButton,
  withUpgradePlanButton,
  onViewPlanPress,
  onUpgradePlanPress,
  onAddUsagePress,
  toggleAddUsageModal,
  percentageIndicatorFillColor,
  percentageIndicatorSecondaryColor,
  percentageIndicatorShouldAnimate,
  images,
  containerStyle,
  nameTextStyle,
  statusTextStyle,
  renewalDateTextStyle,
  numberOfUsersTextStyle,
  usagePercentageContainerStyle,
  percentageIndicatorContainerStyle,
  contractDataContainerStyle,
  usageValuesTextStyle,
  usageViewContainerStyle,
  shouldShowAddUsageButton,
  withUsers,
  isUser,
  withInformationIcon,
  ...props
}) {
  const [shouldShowMoreContractData, setShouldShowMoreContractData] = useState(false);
  const [isQAMVisible, setisQAMVisible] = useState(false);

  const theme = useTheme();
  const SohoImages = getThemeImages(theme.name);

  const firstTwoContractData = _isEmpty(contractData) ? undefined : _slice(contractData, 0, 2);
  const restOfContractData = _isEmpty(contractData) ? undefined : _slice(contractData, 2);

  const onShowMoreOrLessPress = () => {
    setShouldShowMoreContractData(!shouldShowMoreContractData);
  };

  const numberOfUsers = _get(users, 'length', 0);

  const showPrice = withPrice && !_isNil(price);

  const getPriceAndCurrency = () => {
    const totalPrice = withUsers ? (price * numberOfUsers).toFixed(2) : price;
    if (_isNil(currency)) {
      return `${totalPrice}`;
    }
    return isCurrencyFirst ? `${currency}${totalPrice}` : `${totalPrice}${currency}`;
  };
  const renderInformationIconQA = () => {
    return (
      <ConfirmationQuickAction
        isVisible={isQAMVisible}
        title="soho_plan_info_quick_view_title"
        withCloseButton={false}
        onCancelPress={() => setisQAMVisible(false)}
        description={'soho_plan_info_quick_view_description'}
        cancelButtonTitle={'soho_plan_info_quick_view_qam_close_btn_text'}
        withConfirmButton={false}
        showCancelBtn={true}
        cancelButtonTextStyle={styles.cancelButtonTextStyle}
      >
        <PlanInfoQuickView
          id={id}
          usageData={usageData}
          name={name}
          numberOfUsers={numberOfUsers}
          price={price}
          currency={currency}
          isCurrencyFirst={isCurrencyFirst}
        />
      </ConfirmationQuickAction>
    );
  };
  const shouldHideFirstTwoContractDataBottomDivider =
    _isEmpty(restOfContractData) && !withViewPlanButton && !withUpgradePlanButton;
  const renderUsagePart = () => {
    if (!hideProgressBars) {
      if (withPercentageView) {
        return (
          <UsagePercentageView
            planId={id}
            usageData={usageData}
            images={images}
            containerStyle={usagePercentageContainerStyle}
            percentageIndicatorContainerStyle={percentageIndicatorContainerStyle}
            percentageIndicatorFillColor={percentageIndicatorFillColor}
            percentageIndicatorSecondaryColor={percentageIndicatorSecondaryColor}
            percentageIndicatorShouldAnimate={percentageIndicatorShouldAnimate}
            usageValuesTextStyle={usageValuesTextStyle}
            shouldShowAddUsageButton={shouldShowAddUsageButton}
            toggleAddUsageModal={toggleAddUsageModal}
            onAddUsagePress={onAddUsagePress}
            isUser={isUser}
            usageItemLabelTextStyle={{ width: '120%' }}
          />
        );
      } else {
        return (
          <UsageView
            planId={id}
            usageData={usageData}
            images={images}
            containerStyle={usageViewContainerStyle}
            onAddUsagePress={onAddUsagePress}
            toggleAddUsageModal={toggleAddUsageModal}
            shouldShowAddUsageButton={shouldShowAddUsageButton}
            isUser={isUser}
          />
        );
      }
    } else {
      return null;
    }
  };
  return (
    <View style={[styles.containerStyle(theme), containerStyle]}>
      <PlanInfo
        name={name}
        status={status}
        renewalDate={renewalDate}
        nameTextStyle={nameTextStyle}
        statusTextStyle={statusTextStyle}
        renewalDateTextStyle={renewalDateTextStyle}
        withInformationIcon={withInformationIcon}
        onInformationIconPress={() => setisQAMVisible(true)}
      />
      {renderInformationIconQA()}
      {withUsers && numberOfUsers >= 0 ? (
        <View style={styles.usersContainerStyle(theme)}>
          <Icon
            name={usersIcon ? images?.[usersIcon] : SohoImages.ic_business_team_users}
            size={33}
          />
          <VFText
            i18nKey={numberOfUsers > 1 ? 'soho_plans_users_per_plan' : 'soho_plans_user_per_plan'}
            type="primary"
            testKey={SohoFrameworkTestKeys.NumberOfUsersInPlanTitle}
            style={[styles.usersTextStyle, numberOfUsersTextStyle]}
            placeHolders={[numberOfUsers.toString()]}
          />
        </View>
      ) : null}

      {renderUsagePart()}
      {showPrice ? (
        <View style={styles.priceContainerStyle(theme)}>
          <View style={styles.priceDetailsViewStyle(hideProgressBars)}>
            <VFText
              i18nKey="soho_plans_plan_card_price_label"
              type="primary"
              style={styles.priceLabelStyle}
              testKey={SohoFrameworkTestKeys.PlanPricePerMonthTitle}
            />
            <View style={styles.priceAndCurrencyContainer}>
              <VFText
                i18nKey={getPriceAndCurrency()}
                type="primary"
                style={styles.priceTextStyleStyle}
                testKey={SohoFrameworkTestKeys.PlanPriceValue}
              />
            </View>
          </View>
          {withPriceVat ? (
            <VFText
              i18nKey="soho_plans_plan_card_price_includes_vat"
              style={styles.priceIncludeVatTextStyle(theme)}
            />
          ) : null}
        </View>
      ) : null}

      {firstTwoContractData ? (
        <PlanContractInfo
          containerStyle={[
            !showPrice ? styles.contractDataContainerStyle : {},
            shouldHideFirstTwoContractDataBottomDivider
              ? styles.contractDataNoBottomDividerContainerStyle
              : {},
            contractDataContainerStyle,
          ]}
          contractData={firstTwoContractData}
        />
      ) : null}

      {withViewPlanButton || withUpgradePlanButton ? (
        <View
          style={[
            !_isEmpty(firstTwoContractData) ? styles.btnContainerStyle : {},
            !_isEmpty(restOfContractData) ? { ...styles.btnContainerStyleBottom } : {},
          ]}
        >
          <VFButton
            title={
              withViewPlanButton
                ? 'soho_plans_plan_card_view_plan'
                : 'soho_plans_plan_card_upgrade_plan'
            }
            testKey={
              (status === PRODUCT_STATUS.ACTIVE
                ? SohoFrameworkTestKeys.ViewPlanActiveBtn
                : SohoFrameworkTestKeys.ViewPlanInactiveBtn) + id
            }
            onPress={withViewPlanButton ? onViewPlanPress : onUpgradePlanPress}
          />
        </View>
      ) : null}

      {shouldShowMoreContractData ? (
        <PlanContractInfo
          containerStyle={[styles.restOfContractDataContainerStyle, contractDataContainerStyle]}
          contractData={restOfContractData}
          isShowMoreContractData={true}
          labelAccessibilityLabel={SohoFrameworkTestKeys.PlanListDateTitle}
          valueAccessibilityLabel={SohoFrameworkTestKeys.PlanListDateValue}
        />
      ) : null}

      {!_isEmpty(restOfContractData) ? (
        <TouchableWithoutFeedback
          accessibilityLabel={SohoFrameworkTestKeys.PlanShowBtn}
          onPress={onShowMoreOrLessPress}
        >
          <View style={styles.showMoreContainer}>
            <View
              style={
                !shouldShowMoreContractData
                  ? styles.dividerShowMore(theme)
                  : styles.dividerShowLess(theme)
              }
            />
            <VFText
              i18nKey={
                shouldShowMoreContractData
                  ? 'soho_plans_plan_card_show_less'
                  : 'soho_plans_plan_card_show_more'
              }
              type="tertiary"
              style={styles.showMoreText}
            />
          </View>
        </TouchableWithoutFeedback>
      ) : null}
    </View>
  );
}

PlanCard.defaultProps = {
  withPercentageView: true,
  isCurrencyFirst: false,
  withPrice: true,
  withPriceVat: false,
  withViewPlanButton: true,
  withUsers: true,
  isUser: false,
  hideProgressBars: false,
};

PlanCard.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string,
  status: PropTypes.string,
  renewalDate: PropTypes.string,
  usersIcon: PropTypes.string,
  withPercentageView: PropTypes.bool,
  usageData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      usageType: PropTypes.string,
      icon: PropTypes.string,
      isUnlimited: PropTypes.bool,
      totalAmount: PropTypes.number,
      totalUnits: PropTypes.string,
      usedAmount: PropTypes.number,
      usedUnits: PropTypes.string,
      valuePercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.string,
  isCurrencyFirst: PropTypes.bool,
  isUser: PropTypes.bool,
  withPrice: PropTypes.bool,
  withPriceVat: PropTypes.bool,
  contractData: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      role: PropTypes.string,
      name: PropTypes.string,
      '@type': PropTypes.string,
      '@referredType': PropTypes.string,
    }),
  ),
  unassignedUsers: PropTypes.arrayOf(
    PropTypes.shape({
      '@type': PropTypes.string,
      name: PropTypes.string,
      id: PropTypes.string,
      role: PropTypes.string,
    }),
  ),
  withViewPlanButton: PropTypes.bool,
  withUpgradePlanButton: PropTypes.bool,
  onViewPlanPress: PropTypes.func,
  onUpgradePlanPress: PropTypes.func,
  onAddUsagePress: PropTypes.func,
  percentageIndicatorFillColor: PropTypes.string,
  percentageIndicatorSecondaryColor: PropTypes.string,
  percentageIndicatorShouldAnimate: PropTypes.bool,
  images: PropTypes.object,
  containerStyle: ViewPropTypes.style,
  nameTextStyle: TextPropTypes.style,
  statusTextStyle: TextPropTypes.style,
  renewalDateTextStyle: TextPropTypes.style,
  numberOfUsersTextStyle: TextPropTypes.style,
  usagePercentageContainerStyle: ViewPropTypes.style,
  percentageIndicatorContainerStyle: ViewPropTypes.style,
  contractDataContainerStyle: ViewPropTypes.style,
  usageValuesTextStyle: TextPropTypes.style,
  usageViewContainerStyle: ViewPropTypes.style,
  toggleAddUsageModal: PropTypes.func,
  shouldShowAddUsageButton: PropTypes.bool,
  withUsers: PropTypes.bool,
  hideProgressBars: PropTypes.bool,
  withInformationIcon: PropTypes.bool,
};

export default PlanCard;
